import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useListEventsQuery,
  type EventStatusValue,
} from '../../redux/events/events.api';
import { EventCard } from '../../components/EventCard';

const DEBOUNCE_MS = 300;
const PAGE_SIZE = 20;

type StatusFilter = '' | EventStatusValue;

const STATUS_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'finished', label: 'Finished' },
  { value: 'cancelled', label: 'Cancelled' },
];

export const AdminEvents = (): React.JSX.Element => {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setPage(1);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, isFetching, error } = useListEventsQuery({
    search: debouncedSearch || undefined,
    status: status || undefined,
    page,
    limit: PAGE_SIZE,
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const hasItems = data && data.items.length > 0;
  const empty = data && data.items.length === 0;

  return (
    <div className="admin-users">
      <div className="admin-users__header">
        <div className="admin-users__title">
          <h2>Events</h2>
          {data && (
            <span className="admin-users__count">
              {data.total.toLocaleString()} total
            </span>
          )}
        </div>
        <div className="admin-events__filters">
          <select
            className="admin-events__status"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as StatusFilter);
              setPage(1);
            }}
            aria-label="Filter by status"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value || 'all'} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <input
            className="admin-users__search"
            type="search"
            placeholder="Search by event name…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
      </div>

      {error && (
        <div className="admin-users__error">
          Failed to load events. Try again.
        </div>
      )}

      {isLoading && !data && (
        <div className="admin-users__loading">Loading events…</div>
      )}

      {empty && (
        <div className="admin-tab-empty">
          <h2 className="admin-tab-empty__title">No events</h2>
          <p className="admin-tab-empty__sub">
            {debouncedSearch
              ? `No events match "${debouncedSearch}".`
              : 'No events yet.'}
          </p>
        </div>
      )}

      {hasItems && (
        <>
          <div className="admin-events__grid">
            {data!.items.map((event) => (
              <EventCard
                key={event.id}
                event={event}
                onClick={(e) => navigate(`/admin/dashboard/events/${e.id}`)}
              />
            ))}
          </div>

          <div className="admin-users__pagination">
            <button
              type="button"
              className="admin-users__page-btn"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || isFetching}
            >
              ← Previous
            </button>
            <span className="admin-users__page-info">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              className="admin-users__page-btn"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || isFetching}
            >
              Next →
            </button>
          </div>
        </>
      )}
    </div>
  );
};
